import * as React from 'react';
import { SingleValueComponent } from '../common/types';
import styles from './link-heading.scss';
import { LinkContextConsumer } from 'app/components/contexts/link-context';
import { Expandable, ShouldTruncateTextAndLines, truncateStringToMaxLines } from 'ui/expandable';
import { ReactNode, useCallback } from 'react';
import { DisplayLink } from 'ui';
import { useLocation } from 'react-router-dom';
import { isCaseViewModalOpen, shouldOpenCaseInModal } from 'app/components/helpers';

export function useContentRender(value: string, visibleTextLength: number, maxLines: number) {
  return useCallback((collapsed: boolean) => {
    const newValue = value || '';

    return collapsed ? truncateStringToMaxLines(value, maxLines, visibleTextLength) : newValue;
  },
    [value, visibleTextLength, maxLines]
  );
}

export interface PrimaryTitleProps extends SingleValueComponent<string> {
  url?: any;
  children?: ReactNode;
  expandAll?: boolean;
  textDisplayMore: string;
  textDisplayLess: string;
  maxLines?: number;
  visibleTextLength?: number;
  caseId?: number;
  urlOpenNew?: boolean;
  deepLink?: boolean;
  params?: { [key: string]: any };
}

export const LinkHeading: React.FunctionComponent<PrimaryTitleProps> = (props: PrimaryTitleProps) => {
  const { className, value, url, expandAll, textDisplayLess, textDisplayMore, maxLines, visibleTextLength, deepLink } = props;
  const classes = props.url ? styles.link : '';
  const shouldTruncate = ShouldTruncateTextAndLines(value, visibleTextLength, maxLines) && !expandAll;
  const contentRender = useContentRender(value, visibleTextLength, maxLines);
  const {search} = useLocation();
  const resolvedUrl = shouldOpenCaseInModal(!props.params?.id) || isCaseViewModalOpen() ? `${url}${search}` : url;
  
  const valueComponent = shouldTruncate ?
    <Expandable content={contentRender} textDisplayLess={textDisplayLess} textDisplayMore={textDisplayMore} />
    :
    value;
  const printOnly = shouldTruncate ?
    <h1 className={`${styles.printOnly} link-heading-print-only`}>{value}</h1>
    :
    null;
  const titleTruncated = shouldTruncate ? styles.titleTruncated : '';  
  return (
    <LinkContextConsumer>
      {parentLink => parentLink || !url ?
        <>
          <h1 id={props.componentId} className={`${styles.primaryTitle} ${className} ${titleTruncated}`}>
            {valueComponent}
          </h1>
          {printOnly}
        </>
        :
        <DisplayLink url={resolvedUrl} customText={value} className={classes} deepLink={deepLink}>
          <>
            {printOnly}
            <h1 id={props.componentId} className={`${styles.primaryTitle} ${className} ${titleTruncated}`}>
              {valueComponent}
            </h1>
          </>
        </DisplayLink>
      }
    </LinkContextConsumer>);
};

export const ExternalLinkHeading: React.FunctionComponent<PrimaryTitleProps> = (props: PrimaryTitleProps) => {
  const { className, value, url, expandAll, textDisplayLess, textDisplayMore, maxLines, visibleTextLength, 
    urlOpenNew = true } = props;
  const inlineStyle = props.url ? styles.link : '';
  const shouldTruncate = ShouldTruncateTextAndLines(value, visibleTextLength, maxLines) && !expandAll;
  const contentRender = useContentRender(value, visibleTextLength, maxLines);
  const printOnly = shouldTruncate ?
    <h1 className={`${styles.printOnly} link-heading-print-only`}>{value}</h1>
    :
    null;
  const truncatedTitle = shouldTruncate ? styles.titleTruncated : ''; 
  const valueComponent = shouldTruncate ?
    <Expandable content={contentRender} textDisplayLess={textDisplayLess} textDisplayMore={textDisplayMore} />
    :
    value;
  return !url ? (
    <>
      <h1 id={props.componentId} className={`${styles.primaryTitle} ${className} ${truncatedTitle}`}>
        {valueComponent}
      </h1>
      {printOnly}
    </>
  ) : (
    <DisplayLink url={url} className={inlineStyle} urlOpenNew={urlOpenNew}>
      <>
        {printOnly}
        <h1 id={props.componentId} className={`${styles.primaryTitle} ${className} ${truncatedTitle}`}>
          {valueComponent}
        </h1>
      </>
    </DisplayLink>
  );
};
